import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  label?: string;
  showAlpha?: boolean;
  showGradient?: boolean; // Erlaubt Gradient-Auswahl
  allowTransparent?: boolean; // Zeigt "Transparent"-Option
}

// EyeDropper API Types (nicht in allen Browsern verfügbar)
interface EyeDropperResult {
  sRGBHex: string;
}

interface EyeDropperAPI {
  open(): Promise<EyeDropperResult>;
}

declare global {
  interface Window {
    EyeDropper?: new () => EyeDropperAPI;
  }
}

// Check if EyeDropper API is available
const isEyeDropperSupported = typeof window !== 'undefined' && 'EyeDropper' in window;

// Vordefinierte Farben
const PRESET_COLORS = [
  '#000000', '#FFFFFF', '#6B7280', '#EF4444', '#F97316', '#F59E0B',
  '#EAB308', '#84CC16', '#22C55E', '#10B981', '#14B8A6', '#06B6D4',
  '#0EA5E9', '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#D946EF',
  '#EC4899', '#F43F5E',
];

// HSV zu RGB Konvertierung
function hsvToRgb(h: number, s: number, v: number): [number, number, number] {
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  let r = 0, g = 0, b = 0;
  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

// RGB zu HSV Konvertierung
function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;

  if (max !== min) {
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return [h, s, v];
}

// Hex zu RGB
function hexToRgb(hex: string): [number, number, number] | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : null;
}

// RGB zu Hex
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('').toUpperCase();
}

// Gradient-Richtungen - Keys fuer i18n
const GRADIENT_DIRECTION_KEYS = [
  { value: '0deg', label: '↑', key: 'up' },
  { value: '45deg', label: '↗', key: 'upRight' },
  { value: '90deg', label: '→', key: 'right' },
  { value: '135deg', label: '↘', key: 'downRight' },
  { value: '180deg', label: '↓', key: 'down' },
  { value: '225deg', label: '↙', key: 'downLeft' },
  { value: '270deg', label: '←', key: 'left' },
  { value: '315deg', label: '↖', key: 'upLeft' },
];

// Vordefinierte Gradient-Presets (Multi-Stop Support) - Keys fuer i18n
const GRADIENT_PRESET_KEYS = [
  { stops: [{ color: '#667eea', position: 0 }, { color: '#764ba2', position: 100 }], key: 'violetPurple' },
  { stops: [{ color: '#f093fb', position: 0 }, { color: '#f5576c', position: 100 }], key: 'pinkRed' },
  { stops: [{ color: '#4facfe', position: 0 }, { color: '#00f2fe', position: 100 }], key: 'blueTurquoise' },
  { stops: [{ color: '#43e97b', position: 0 }, { color: '#38f9d7', position: 100 }], key: 'greenTurquoise' },
  { stops: [{ color: '#fa709a', position: 0 }, { color: '#fee140', position: 100 }], key: 'pinkYellow' },
  { stops: [{ color: '#30cfd0', position: 0 }, { color: '#330867', position: 100 }], key: 'turquoiseDarkBlue' },
  { stops: [{ color: '#a8edea', position: 0 }, { color: '#fed6e3', position: 100 }], key: 'pastel' },
  { stops: [{ color: '#000000', position: 0 }, { color: '#434343', position: 100 }], key: 'blackGray' },
  // Multi-Stop Presets
  { stops: [{ color: '#FF6B6B', position: 0 }, { color: '#FFE66D', position: 50 }, { color: '#4ECDC4', position: 100 }], key: 'sunset' },
  { stops: [{ color: '#667eea', position: 0 }, { color: '#764ba2', position: 33 }, { color: '#f093fb', position: 66 }, { color: '#f5576c', position: 100 }], key: 'aurora' },
  { stops: [{ color: '#0F2027', position: 0 }, { color: '#203A43', position: 50 }, { color: '#2C5364', position: 100 }], key: 'midnight' },
  { stops: [{ color: '#f12711', position: 0 }, { color: '#f5af19', position: 100 }], key: 'fire' },
];

// Color Stop Interface
interface ColorStop {
  id: string;
  color: string;
  position: number; // 0-100
}

// Generate unique ID for color stops
function generateStopId(): string {
  return Math.random().toString(36).substring(2, 9);
}

// Parse multi-stop gradient string
function parseMultiStopGradient(gradient: string): { stops: ColorStop[]; direction: string; type: 'linear' | 'radial' } {
  // Default values
  const defaultStops: ColorStop[] = [
    { id: generateStopId(), color: '#667eea', position: 0 },
    { id: generateStopId(), color: '#764ba2', position: 100 }
  ];

  if (!gradient) {
    return { stops: defaultStops, direction: '180deg', type: 'linear' };
  }

  // Detect gradient type
  const isRadial = gradient.startsWith('radial-gradient');
  const type: 'linear' | 'radial' = isRadial ? 'radial' : 'linear';

  // Extract direction/position
  let direction = '180deg';
  const dirMatch = gradient.match(/linear-gradient\((\d+deg)/);
  if (dirMatch) {
    direction = dirMatch[1];
  }

  // Extract all color stops with regex
  // Matches: #RRGGBB XX% or #RGB XX% or color-name XX%
  const stopRegex = /(#[0-9A-Fa-f]{3,6}|rgb\([^)]+\)|rgba\([^)]+\))\s+(\d+(?:\.\d+)?)%/g;
  const stops: ColorStop[] = [];
  let match;

  while ((match = stopRegex.exec(gradient)) !== null) {
    let color = match[1];
    // Normalize short hex to full hex
    if (color.match(/^#[0-9A-Fa-f]{3}$/)) {
      color = '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3];
    }
    stops.push({
      id: generateStopId(),
      color: color.toUpperCase(),
      position: parseFloat(match[2])
    });
  }

  // Sort by position
  stops.sort((a, b) => a.position - b.position);

  return {
    stops: stops.length >= 2 ? stops : defaultStops,
    direction,
    type
  };
}

// Build gradient string from multi-stop data
function buildMultiStopGradient(stops: ColorStop[], direction: string, type: 'linear' | 'radial' = 'linear'): string {
  if (stops.length < 2) {
    return `linear-gradient(${direction}, #667eea 0%, #764ba2 100%)`;
  }

  // Sort stops by position
  const sortedStops = [...stops].sort((a, b) => a.position - b.position);
  const stopStrings = sortedStops.map(s => `${s.color} ${s.position}%`).join(', ');

  if (type === 'radial') {
    return `radial-gradient(circle, ${stopStrings})`;
  }
  return `linear-gradient(${direction}, ${stopStrings})`;
}

// Multi-Stop Gradient Picker Komponente (Figma/Photoshop-style)
interface GradientPickerProps {
  value: string;
  onChange: (gradient: string) => void;
  label?: string;
  maxStops?: number; // Default: 10
}

export function GradientPicker({ value, onChange, label, maxStops = 10 }: GradientPickerProps) {
  const { t } = useTranslation(['creator', 'common']);
  const parsed = parseMultiStopGradient(value);
  const [stops, setStops] = useState<ColorStop[]>(parsed.stops);
  const [direction, setDirection] = useState(parsed.direction);
  const [gradientType, setGradientType] = useState<'linear' | 'radial'>(parsed.type);
  const [selectedStopId, setSelectedStopId] = useState<string | null>(parsed.stops[0]?.id || null);
  const [showPresets, setShowPresets] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const gradientBarRef = useRef<HTMLDivElement>(null);

  // Get selected stop
  const selectedStop = stops.find(s => s.id === selectedStopId);

  // Update gradient when any value changes (wrapper that marks internal updates)
  const updateGradient = useCallback((newStops: ColorStop[], dir: string, type: 'linear' | 'radial') => {
    isInternalUpdate.current = true;
    const newValue = buildMultiStopGradient(newStops, dir, type);
    lastExternalValue.current = newValue;
    onChange(newValue);
  }, [onChange]);

  // Sync with external value - but only on initial mount or structural changes
  // We use a ref to track if this is an internal update (from our own onChange)
  const isInternalUpdate = useRef(false);
  const lastExternalValue = useRef(value);

  useEffect(() => {
    // Skip if this is our own update
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    // Only re-parse if the value actually changed from external source
    if (value !== lastExternalValue.current) {
      lastExternalValue.current = value;
      const newParsed = parseMultiStopGradient(value);

      // Check if structure changed (number of stops or significant position change)
      const structureChanged = newParsed.stops.length !== stops.length;

      if (structureChanged || stops.length === 0) {
        setStops(newParsed.stops);
        setDirection(newParsed.direction);
        setGradientType(newParsed.type);
        setSelectedStopId(newParsed.stops[0]?.id || null);
      }
    }
  }, [value]);

  // Add new color stop
  const handleAddStop = (position: number) => {
    if (stops.length >= maxStops) return;

    // Find colors to interpolate between
    const sortedStops = [...stops].sort((a, b) => a.position - b.position);
    let leftStop = sortedStops[0];
    let rightStop = sortedStops[sortedStops.length - 1];

    for (let i = 0; i < sortedStops.length - 1; i++) {
      if (sortedStops[i].position <= position && sortedStops[i + 1].position >= position) {
        leftStop = sortedStops[i];
        rightStop = sortedStops[i + 1];
        break;
      }
    }

    // Interpolate color
    const factor = (position - leftStop.position) / (rightStop.position - leftStop.position || 1);
    const rgb1 = hexToRgb(leftStop.color);
    const rgb2 = hexToRgb(rightStop.color);
    let newColor = '#888888';
    if (rgb1 && rgb2) {
      const r = Math.round(rgb1[0] + (rgb2[0] - rgb1[0]) * factor);
      const g = Math.round(rgb1[1] + (rgb2[1] - rgb1[1]) * factor);
      const b = Math.round(rgb1[2] + (rgb2[2] - rgb1[2]) * factor);
      newColor = rgbToHex(r, g, b);
    }

    const newStop: ColorStop = {
      id: generateStopId(),
      color: newColor,
      position: Math.round(position)
    };

    const newStops = [...stops, newStop];
    setStops(newStops);
    setSelectedStopId(newStop.id);
    updateGradient(newStops, direction, gradientType);
  };

  // Update stop color
  const handleStopColorChange = (stopId: string, color: string) => {
    if (color === 'transparent') color = '#FFFFFF';
    const newStops = stops.map(s => s.id === stopId ? { ...s, color } : s);
    setStops(newStops);
    updateGradient(newStops, direction, gradientType);
  };

  // Update stop position
  const handleStopPositionChange = (stopId: string, position: number) => {
    const clampedPosition = Math.max(0, Math.min(100, Math.round(position)));
    const newStops = stops.map(s => s.id === stopId ? { ...s, position: clampedPosition } : s);
    setStops(newStops);
    updateGradient(newStops, direction, gradientType);
  };

  // Delete stop (minimum 2 stops required)
  const handleDeleteStop = (stopId: string) => {
    if (stops.length <= 2) return;
    const newStops = stops.filter(s => s.id !== stopId);
    setStops(newStops);
    if (selectedStopId === stopId) {
      setSelectedStopId(newStops[0]?.id || null);
    }
    updateGradient(newStops, direction, gradientType);
  };

  // Handle direction change
  const handleDirectionChange = (dir: string) => {
    setDirection(dir);
    updateGradient(stops, dir, gradientType);
  };

  // Handle gradient type change
  const handleTypeChange = (type: 'linear' | 'radial') => {
    setGradientType(type);
    updateGradient(stops, direction, type);
  };

  // Reverse gradient
  const handleReverse = () => {
    const newStops = stops.map(s => ({ ...s, position: 100 - s.position }));
    setStops(newStops);
    updateGradient(newStops, direction, gradientType);
  };

  // Distribute stops evenly
  const handleDistributeEvenly = () => {
    const sortedStops = [...stops].sort((a, b) => a.position - b.position);
    const newStops = sortedStops.map((s, i) => ({
      ...s,
      position: Math.round((i / (sortedStops.length - 1)) * 100)
    }));
    setStops(newStops);
    updateGradient(newStops, direction, gradientType);
  };

  // Handle preset selection
  const handlePresetSelect = (preset: typeof GRADIENT_PRESET_KEYS[0]) => {
    const newStops = preset.stops.map(s => ({ ...s, id: generateStopId() }));
    setStops(newStops);
    setSelectedStopId(newStops[0]?.id || null);
    updateGradient(newStops, direction, gradientType);
    setShowPresets(false);
  };

  // Handle gradient bar click to add stop
  const handleBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) return;
    const rect = gradientBarRef.current?.getBoundingClientRect();
    if (!rect) return;
    const position = ((e.clientX - rect.left) / rect.width) * 100;
    handleAddStop(position);
  };

  // Handle stop drag
  const handleStopDrag = (e: React.MouseEvent, stopId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setSelectedStopId(stopId);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const rect = gradientBarRef.current?.getBoundingClientRect();
      if (!rect) return;
      const position = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      handleStopPositionChange(stopId, position);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Current gradient CSS
  const gradientCSS = buildMultiStopGradient(stops, direction, gradientType);
  const barGradientCSS = buildMultiStopGradient(stops, '90deg', 'linear');

  return (
    <div className="space-y-3">
      {label && (
        <label className="block text-sm font-medium text-gray-700">{label}</label>
      )}

      {/* Preview */}
      <div
        className="h-16 rounded-lg border border-gray-300 shadow-inner"
        style={{ background: gradientCSS }}
      />

      {/* Gradient Type Toggle */}
      <div className="flex items-center gap-2">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => handleTypeChange('linear')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              gradientType === 'linear'
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t('creator:colorPicker.linear')}
          </button>
          <button
            type="button"
            onClick={() => handleTypeChange('radial')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              gradientType === 'radial'
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t('creator:colorPicker.radial')}
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex gap-1 ml-auto">
          <button
            type="button"
            onClick={handleReverse}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            title={t('creator:colorPicker.reverse')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
          </button>
          <button
            type="button"
            onClick={handleDistributeEvenly}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            title={t('creator:colorPicker.distributeEvenly')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Direction Selector (only for linear) */}
      {gradientType === 'linear' && (
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('creator:colorPicker.direction')}</label>
          <div className="grid grid-cols-8 gap-1">
            {GRADIENT_DIRECTION_KEYS.map((dir) => (
              <button
                key={dir.value}
                type="button"
                onClick={() => handleDirectionChange(dir.value)}
                className={`p-1.5 text-base rounded border transition-all ${
                  direction === dir.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
                title={t(`creator:colorPicker.directions.${dir.key}`)}
              >
                {dir.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Gradient Bar with Color Stops */}
      <div className="pt-2">
        <div className="flex justify-between items-center mb-1">
          <label className="text-xs text-gray-500">{t('creator:colorPicker.colorStops')}</label>
          <span className="text-xs text-gray-400">
            {stops.length} / {maxStops} {stops.length < maxStops && t('creator:colorPicker.clickToAdd')}
          </span>
        </div>
        <div
          ref={gradientBarRef}
          className="relative h-6 rounded-lg cursor-crosshair"
          style={{ background: barGradientCSS }}
          onClick={handleBarClick}
        >
          {/* Checkered background for transparency */}
          <div
            className="absolute inset-0 rounded-lg -z-10"
            style={{
              backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
              backgroundSize: '8px 8px',
              backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px'
            }}
          />

          {/* Color Stop Handles */}
          {stops.map((stop) => (
            <div
              key={stop.id}
              className={`absolute top-0 -translate-x-1/2 cursor-grab active:cursor-grabbing select-none ${
                selectedStopId === stop.id ? 'z-20' : 'z-10'
              }`}
              style={{ left: `${stop.position}%` }}
              onMouseDown={(e) => handleStopDrag(e, stop.id)}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStopId(stop.id);
              }}
            >
              {/* Triangle pointer */}
              <div
                className={`w-0 h-0 mx-auto border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent ${
                  selectedStopId === stop.id ? 'border-b-blue-500' : 'border-b-gray-400'
                }`}
              />
              {/* Color swatch */}
              <div
                className={`w-4 h-4 rounded-sm border-2 shadow-sm ${
                  selectedStopId === stop.id ? 'border-blue-500 ring-2 ring-blue-200' : 'border-white'
                }`}
                style={{ backgroundColor: stop.color }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Selected Stop Editor */}
      {selectedStop && (
        <div className="p-3 bg-gray-50 rounded-lg space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600">{t('creator:colorPicker.selectedStop')}</span>
            {stops.length > 2 && (
              <button
                type="button"
                onClick={() => handleDeleteStop(selectedStop.id)}
                className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {t('common:delete')}
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Color picker for selected stop */}
            <div className="flex-1">
              <ColorPicker
                color={selectedStop.color}
                onChange={(c) => handleStopColorChange(selectedStop.id, c)}
                label={t('creator:color')}
              />
            </div>

            {/* Position input */}
            <div className="w-20">
              <label className="block text-xs text-gray-500 mb-1">{t('creator:colorPicker.stopPosition')}</label>
              <div className="flex items-center">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={selectedStop.position}
                  onChange={(e) => handleStopPositionChange(selectedStop.id, parseInt(e.target.value) || 0)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-l focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="px-2 py-1.5 text-sm bg-gray-100 border border-l-0 border-gray-300 rounded-r text-gray-500">%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Stop Button */}
      {stops.length < maxStops && (
        <button
          type="button"
          onClick={() => handleAddStop(50)}
          className="w-full py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg border border-dashed border-blue-300 transition-colors"
        >
          {t('creator:colorPicker.addColorStop')}
        </button>
      )}

      {/* Presets Toggle */}
      <div>
        <button
          type="button"
          onClick={() => setShowPresets(!showPresets)}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          <svg className={`w-4 h-4 transition-transform ${showPresets ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          {t('creator:colorPicker.presets')}
        </button>

        {showPresets && (
          <div className="mt-2 grid grid-cols-4 gap-2">
            {GRADIENT_PRESET_KEYS.map((preset, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handlePresetSelect(preset)}
                className="h-10 rounded-lg border border-gray-200 hover:border-blue-400 transition-colors overflow-hidden"
                style={{ background: buildMultiStopGradient(preset.stops.map(s => ({ ...s, id: 'temp' })), '180deg', 'linear') }}
                title={t(`creator:colorPicker.gradients.${preset.key}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function ColorPicker({ color, onChange, label, showAlpha = false, allowTransparent = false }: ColorPickerProps) {
  const { t } = useTranslation(['creator']);
  const [isOpen, setIsOpen] = useState(false);
  const [hexInput, setHexInput] = useState(color === 'transparent' ? '' : color);
  const [hsv, setHsv] = useState<[number, number, number]>([0, 1, 1]);
  const [alpha, setAlpha] = useState(1);
  const [isPickingColor, setIsPickingColor] = useState(false);
  const [isTransparent, setIsTransparent] = useState(color === 'transparent');

  const pickerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const saturationRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false); // Use ref for immediate update (no race condition)
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

  // EyeDropper / Pipette Funktion
  const handleEyeDropper = useCallback(async () => {
    if (!isEyeDropperSupported || !window.EyeDropper) return;

    try {
      setIsPickingColor(true);
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();

      // Setze die aufgenommene Farbe
      const rgb = hexToRgb(result.sRGBHex);
      if (rgb) {
        const newHsv = rgbToHsv(...rgb);
        setHsv(newHsv);
        setHexInput(result.sRGBHex.toUpperCase());
        onChange(result.sRGBHex.toUpperCase());
      }
    } catch {
      // User cancelled or error occurred
    } finally {
      setIsPickingColor(false);
    }
  }, [onChange]);

  // Parse initial color
  useEffect(() => {
    if (color === 'transparent') {
      setIsTransparent(true);
      setHexInput('');
    } else if (color.startsWith('#')) {
      setIsTransparent(false);
      const rgb = hexToRgb(color);
      if (rgb) {
        setHsv(rgbToHsv(...rgb));
        setHexInput(color);
      }
    } else if (color.startsWith('rgba')) {
      const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
      if (match) {
        const [, r, g, b, a] = match;
        setIsTransparent(false);
        setHsv(rgbToHsv(parseInt(r), parseInt(g), parseInt(b)));
        setAlpha(a ? parseFloat(a) : 1);
        setHexInput(rgbToHex(parseInt(r), parseInt(g), parseInt(b)));
      }
    }
  }, [color]);

  // Calculate popup position when opening
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const popupWidth = 264; // w-64 = 16rem = 256px + padding
      const popupHeight = 400; // Approximate height with all elements
      const margin = 8; // Gap between trigger and popup

      // Calculate available space
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const spaceRight = viewportWidth - rect.left;

      // Determine vertical position
      let top: number;
      if (spaceBelow >= popupHeight + margin) {
        // Enough space below - open downwards
        top = rect.bottom + window.scrollY + margin;
      } else if (spaceAbove >= popupHeight + margin) {
        // Not enough space below, but enough above - open upwards
        top = rect.top + window.scrollY - popupHeight - margin;
      } else {
        // Not enough space either way - position to fit in viewport
        // Prefer below if more space, otherwise above
        if (spaceBelow >= spaceAbove) {
          top = rect.bottom + window.scrollY + margin;
        } else {
          top = Math.max(margin + window.scrollY, rect.top + window.scrollY - popupHeight - margin);
        }
      }

      // Determine horizontal position
      let left: number;
      if (spaceRight >= popupWidth) {
        // Enough space to the right - align with trigger left edge
        left = rect.left + window.scrollX;
      } else {
        // Not enough space - shift left to fit in viewport
        left = Math.max(margin, viewportWidth - popupWidth - margin) + window.scrollX;
      }

      setPopupPosition({ top, left });
    }
  }, [isOpen]);

  // Close picker on outside click
  // Using 'mousedown' in capture phase to detect clicks before they reach the picker
  useEffect(() => {
    const handleMouseDownOutside = (e: MouseEvent) => {
      // Don't close if currently dragging
      if (isDraggingRef.current) {
        return;
      }

      const target = e.target as Node;

      // Check if mousedown is inside the popup - if so, don't close
      if (pickerRef.current && pickerRef.current.contains(target)) {
        return;
      }

      // Check if mousedown is inside the trigger - if so, don't close (toggle handled by button)
      if (triggerRef.current && triggerRef.current.contains(target)) {
        return;
      }

      // Click is outside both trigger and popup - close the picker
      setIsOpen(false);
    };

    if (isOpen) {
      // Small delay to prevent immediate closing when opening
      const timer = setTimeout(() => {
        // Use capture phase to get the event before it reaches child elements
        document.addEventListener('mousedown', handleMouseDownOutside, true);
      }, 10);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleMouseDownOutside, true);
      };
    }
  }, [isOpen]);

  // Update color from HSV
  const updateColor = (h: number, s: number, v: number, a: number = alpha) => {
    setIsTransparent(false);
    setHsv([h, s, v]);
    const rgb = hsvToRgb(h, s, v);
    const hex = rgbToHex(...rgb);
    setHexInput(hex);

    if (showAlpha && a < 1) {
      onChange(`rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})`);
    } else {
      onChange(hex);
    }
  };

  // Set transparent
  const setTransparent = () => {
    setIsTransparent(true);
    setHexInput('');
    onChange('transparent');
  };

  // Handle saturation/value picker with drag support
  const handleSaturationMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingRef.current = true; // Set ref immediately (synchronous)
    const rect = saturationRef.current?.getBoundingClientRect();
    if (!rect) return;

    const updateSaturation = (clientX: number, clientY: number) => {
      const s = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const v = Math.max(0, Math.min(1, 1 - (clientY - rect.top) / rect.height));
      updateColor(hsv[0], s, v);
    };

    // Initial click position
    updateSaturation(e.clientX, e.clientY);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      updateSaturation(moveEvent.clientX, moveEvent.clientY);
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Handle hue slider with drag support
  const handleHueMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingRef.current = true; // Set ref immediately (synchronous)
    const rect = hueRef.current?.getBoundingClientRect();
    if (!rect) return;

    const updateHue = (clientX: number) => {
      const h = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      updateColor(h, hsv[1], hsv[2]);
    };

    // Initial click position
    updateHue(e.clientX);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      updateHue(moveEvent.clientX);
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Handle hex input
  const handleHexChange = (value: string) => {
    setHexInput(value);
    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
      const rgb = hexToRgb(value);
      if (rgb) {
        const newHsv = rgbToHsv(...rgb);
        setHsv(newHsv);
        onChange(value);
      }
    }
  };

  // Calculate current color
  const currentRgb = hsvToRgb(...hsv);
  const currentHex = rgbToHex(...currentRgb);

  return (
    <div className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}

      {/* Color Preview Button with Eyedropper */}
      <div className="flex items-center gap-1" ref={triggerRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex-1 flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:border-gray-400"
        >
          <div
            className="w-6 h-6 rounded border border-gray-300 relative overflow-hidden"
            style={{ backgroundColor: isTransparent ? undefined : currentHex }}
          >
            {/* Checkerboard pattern for transparent */}
            {isTransparent && (
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `linear-gradient(45deg, #ccc 25%, transparent 25%),
                    linear-gradient(-45deg, #ccc 25%, transparent 25%),
                    linear-gradient(45deg, transparent 75%, #ccc 75%),
                    linear-gradient(-45deg, transparent 75%, #ccc 75%)`,
                  backgroundSize: '8px 8px',
                  backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
                }}
              />
            )}
          </div>
          <span className="text-sm text-gray-700 font-mono">
            {isTransparent ? t('creator:colorPicker.transparent') : currentHex}
          </span>
        </button>

        {/* Pipette / EyeDropper Button */}
        {isEyeDropperSupported && (
          <button
            type="button"
            onClick={handleEyeDropper}
            disabled={isPickingColor}
            className={`p-2 border rounded-lg transition-colors ${
              isPickingColor
                ? 'border-blue-500 bg-blue-50 text-blue-600'
                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            }`}
            title={t('creator:colorPicker.pickColor')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Picker Dropdown - rendered as Portal to avoid z-index issues */}
      {isOpen && createPortal(
        <div
          ref={pickerRef}
          className="fixed z-[9999] p-3 bg-white rounded-lg shadow-xl border border-gray-200 w-64"
          style={{ top: popupPosition.top, left: popupPosition.left }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Saturation/Value Picker */}
          <div
            ref={saturationRef}
            className="relative w-full h-40 rounded cursor-crosshair mb-3 select-none"
            style={{
              background: `linear-gradient(to bottom, transparent, black),
                           linear-gradient(to right, white, hsl(${hsv[0] * 360}, 100%, 50%))`,
            }}
            onMouseDown={handleSaturationMouseDown}
          >
            {/* Selector */}
            <div
              className="absolute w-4 h-4 border-2 border-white rounded-full shadow-md -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              style={{
                left: `${hsv[1] * 100}%`,
                top: `${(1 - hsv[2]) * 100}%`,
                backgroundColor: currentHex,
              }}
            />
          </div>

          {/* Hue Slider */}
          <div
            ref={hueRef}
            className="relative w-full h-3 rounded cursor-pointer mb-3 select-none"
            style={{
              background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
            }}
            onMouseDown={handleHueMouseDown}
          >
            <div
              className="absolute w-4 h-4 border-2 border-white rounded-full shadow-md -translate-x-1/2 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{
                left: `${hsv[0] * 100}%`,
                backgroundColor: `hsl(${hsv[0] * 360}, 100%, 50%)`,
              }}
            />
          </div>

          {/* Alpha Slider */}
          {showAlpha && (
            <div className="mb-3">
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={alpha * 100}
                  onChange={(e) => {
                    const a = parseInt(e.target.value) / 100;
                    setAlpha(a);
                    updateColor(hsv[0], hsv[1], hsv[2], a);
                  }}
                  className="flex-1"
                />
                <span className="text-sm text-gray-500 w-10">{Math.round(alpha * 100)}%</span>
              </div>
            </div>
          )}

          {/* Hex Input */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm text-gray-500">{t('creator:colorPicker.hex')}:</span>
            <input
              type="text"
              value={hexInput}
              onChange={(e) => handleHexChange(e.target.value)}
              className="flex-1 px-2 py-1 text-sm font-mono border border-gray-300 rounded"
              placeholder="#000000"
            />
          </div>

          {/* Transparent Option */}
          {allowTransparent && (
            <button
              type="button"
              onClick={setTransparent}
              className={`w-full mb-3 px-3 py-2 text-sm rounded border flex items-center gap-2 ${
                isTransparent
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div
                className="w-5 h-5 rounded border border-gray-300 relative overflow-hidden"
                style={{
                  backgroundImage: `linear-gradient(45deg, #ccc 25%, transparent 25%),
                    linear-gradient(-45deg, #ccc 25%, transparent 25%),
                    linear-gradient(45deg, transparent 75%, #ccc 75%),
                    linear-gradient(-45deg, transparent 75%, #ccc 75%)`,
                  backgroundSize: '6px 6px',
                  backgroundPosition: '0 0, 0 3px, 3px -3px, -3px 0px',
                }}
              />
              <span>{t('creator:colorPicker.transparent')}</span>
            </button>
          )}

          {/* Preset Colors */}
          <div className="grid grid-cols-10 gap-1">
            {PRESET_COLORS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={`w-5 h-5 rounded border ${
                  !isTransparent && currentHex.toUpperCase() === preset.toUpperCase()
                    ? 'border-blue-500 ring-1 ring-blue-500'
                    : 'border-gray-300'
                }`}
                style={{ backgroundColor: preset }}
                onClick={() => {
                  const rgb = hexToRgb(preset);
                  if (rgb) {
                    const newHsv = rgbToHsv(...rgb);
                    updateColor(newHsv[0], newHsv[1], newHsv[2]);
                  }
                }}
              />
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
